import { useState, useEffect, useRef, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import { useNavigate, useSearchParams } from "react-router-dom";
import RecipeForm from "../components/RecipeForm";
import ConfirmModal from "../components/ConfirmModal";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "../lib/toast";
import { deriveUniformTimeFields, toMinutes } from "../lib/recipeTime";

export default function RecipeCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedVisibility = ["public", "private"].includes(searchParams.get("view"))
    ? searchParams.get("view")
    : "";
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ingredients: "",
    instructions: "",
    prep_time_value: "",
    prep_time_unit: "min",
    cook_time_value: "",
    cook_time_unit: "min",
    servings: "",
    difficulty: "",
    category: "",
    diet_preference: "",
    visibility: requestedVisibility,
    source_author_name: "",
    source_url: "",
    images: [],
    videos: [],
    tag_names: [],
  });

  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importingRecipe, setImportingRecipe] = useState(false);
  const [showImportUrlModal, setShowImportUrlModal] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importingUrl, setImportingUrl] = useState(false);
  const uploadRecipeInputRef = useRef(null);

  // Detect unsaved changes
  const guardPushedRef = useRef(false);
  const leavingRef = useRef(false);
  const pendingLeaveActionRef = useRef("back");
  const hasUnsavedData = useCallback(() => {
    const visibilityChanged = requestedVisibility
      ? formData.visibility !== requestedVisibility
      : !!formData.visibility;

    return !!(formData.title || formData.description || formData.ingredients ||
      formData.instructions || formData.prep_time_value || formData.cook_time_value ||
      formData.servings || formData.difficulty || formData.category ||
      formData.diet_preference || visibilityChanged ||
      formData.source_author_name || formData.source_url ||
      formData.images?.length || formData.videos?.length ||
      formData.tag_names?.length);
  }, [formData, requestedVisibility]);

  // Block browser refresh/close
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedData() && !showSuccess && !leavingRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedData, showSuccess]);

  // Push one guard entry when form first becomes dirty
  useEffect(() => {
    if (
      hasUnsavedData() &&
      !guardPushedRef.current &&
      !leavingRef.current &&
      !showSuccess &&
      !showLeaveConfirm
    ) {
      window.history.pushState({ guard: true }, '', window.location.href);
      guardPushedRef.current = true;
    }
  }, [hasUnsavedData, showSuccess, showLeaveConfirm]);

  const confirmLeave = () => {
    const leaveAction = pendingLeaveActionRef.current;
    leavingRef.current = true;
    setShowLeaveConfirm(false);
    guardPushedRef.current = false;

    if (leaveAction === "recipes") {
      navigate("/recipes");
      return;
    }

    window.history.back();

    // Safety net: if the browser only popped a same-page guard entry, do not
    // leave the user stranded with leavingRef stuck on true.
    window.setTimeout(() => {
      if (window.location.pathname === "/recipes/create") {
        navigate("/recipes", { replace: true });
        return;
      }
      leavingRef.current = false;
    }, 150);
  };

  // Block browser back — only catches the guard entry pop
  useEffect(() => {
    const handler = () => {
      if (leavingRef.current) return;
      if (guardPushedRef.current && hasUnsavedData() && !showSuccess) {
        // Back popped our guard entry. Wait for the user's choice before
        // deciding whether to restore the guard or continue leaving.
        guardPushedRef.current = false;
        pendingLeaveActionRef.current = "back";
        setShowLeaveConfirm(true);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [hasUnsavedData, showSuccess]);

  // Scan recipe state
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanUrl, setScanUrl] = useState("");
  const [scanToken, setScanToken] = useState("");
  const [scanStatus, setScanStatus] = useState("idle"); // idle | waiting | done | failed
  const pollRef = useRef(null);
  const pollErrorCountRef = useRef(0);
  const pollStartedAtRef = useRef(null);
  const POLL_MAX_DURATION_MS = 3 * 60 * 1000;

  const startScan = async () => {
    try {
      const res = await axiosInstance.post("/api/scan/create/");
      const token = res.data.token;
      const origin = window.location.origin; // e.g., http://192.168.4.41:3000 or https://cartnom.com
      setScanToken(token);
      setScanUrl(`${origin}/scan/${token}`);
      setScanStatus("waiting");
      setShowScanModal(true);
    } catch (err) {
      console.error("Failed to create scan session:", err);
      toast.error("Failed to start scan. Please try again.");
    }
  };

  const applyParsedData = useCallback((d) => {
    setFormData((prev) => {
      const hasPrepTime = d.prep_time !== undefined && d.prep_time !== null && d.prep_time !== "";
      const hasCookTime = d.cook_time !== undefined && d.cook_time !== null && d.cook_time !== "";
      const nextTimeFields = hasPrepTime || hasCookTime
        ? deriveUniformTimeFields(
            hasPrepTime ? d.prep_time : toMinutes(prev.prep_time_value, prev.prep_time_unit),
            hasCookTime ? d.cook_time : toMinutes(prev.cook_time_value, prev.cook_time_unit)
          )
        : {
            prep_time_value: prev.prep_time_value,
            prep_time_unit: prev.prep_time_unit,
            cook_time_value: prev.cook_time_value,
            cook_time_unit: prev.cook_time_unit,
          };

      return {
        ...prev,
        ...nextTimeFields,
        title: d.title || prev.title,
        description: d.description || prev.description,
        ingredients: (d.ingredients || []).join("\n") || prev.ingredients,
        instructions: (d.instructions || []).join("\n") || prev.instructions,
        servings: d.servings ?? prev.servings,
        difficulty: d.difficulty || prev.difficulty,
        diet_preference: d.diet_preference || prev.diet_preference,
        category: d.category || prev.category,
        source_author_name: d.source_author_name || prev.source_author_name,
        source_url: d.source_url || prev.source_url,
        tag_names: d.tag_names?.length ? d.tag_names : prev.tag_names,
      };
    });
  }, []);

  const handleUploadRecipeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg("");
    setImportingRecipe(true);

    try {
      const createRes = await axiosInstance.post("/api/scan/create/");
      const token = createRes.data.token;

      const data = new FormData();
      data.append("file", file);

      await axiosInstance.post(`/api/scan/${token}/upload-document/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const finishRes = await axiosInstance.post(`/api/scan/${token}/finish/`);
      if (finishRes.data.status !== "done" || !finishRes.data.parsed_data) {
        throw new Error("Import failed");
      }

      applyParsedData(finishRes.data.parsed_data);
      toast.success("Recipe imported from file.");
    } catch (err) {
      console.error(err);
      const message = getCreateRecipeErrorMessage(err);
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setImportingRecipe(false);
      e.target.value = "";
    }
  };

  const handleImportUrl = async () => {
    if (!importUrl.trim()) {
      setErrorMsg("Please enter a recipe URL.");
      toast.error("Please enter a recipe URL.");
      return;
    }

    setErrorMsg("");
    setImportingUrl(true);

    try {
      const res = await axiosInstance.post("/api/recipes/import-url/", {
        url: importUrl.trim(),
      });

      if (res.data.status !== "done" || !res.data.parsed_data) {
        throw new Error("Import failed");
      }

      applyParsedData(res.data.parsed_data);
      setShowImportUrlModal(false);
      setImportUrl("");
      toast.success("Recipe imported from URL.");
    } catch (err) {
      console.error(err);
      const message = getCreateRecipeErrorMessage(err);
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setImportingUrl(false);
    }
  };

  // Poll for scan result
  useEffect(() => {
    if (scanStatus !== "waiting" || !scanToken) return;

    pollErrorCountRef.current = 0;
    pollStartedAtRef.current = Date.now();

    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartedAtRef.current > POLL_MAX_DURATION_MS) {
        clearInterval(pollRef.current);
        setScanStatus("failed");
        return;
      }

      try {
        const res = await axiosInstance.get(`/api/scan/${scanToken}/status/`);
        pollErrorCountRef.current = 0;

        if (res.data.status === "done" && res.data.parsed_data) {
          clearInterval(pollRef.current);
          setScanStatus("done");
          applyParsedData(res.data.parsed_data);
          setShowScanModal(false);
        } else if (res.data.status === "failed") {
          clearInterval(pollRef.current);
          setScanStatus("failed");
        }
      } catch {
        pollErrorCountRef.current += 1;
        if (pollErrorCountRef.current >= 3) {
          clearInterval(pollRef.current);
          setScanStatus("failed");
        }
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [applyParsedData, scanStatus, scanToken, POLL_MAX_DURATION_MS]);

  const closeScanModal = () => {
    setShowScanModal(false);
    setScanStatus("idle");
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "images" || name === "videos") {
      setFormData({ ...formData, [name]: [...files] });
    } else if (name === "tag_names") {
      setFormData({ ...formData, tag_names: value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const getCreateRecipeErrorMessage = (err) => {
    const data = err.response?.data;

    if (!data) {
      return "Failed to create recipe. Please try again.";
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.detail) {
      return Array.isArray(data.detail) ? data.detail[0] : String(data.detail);
    }

    if (typeof data === "object") {
      const messages = Object.entries(data).flatMap(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];
        return values.map((message) => `${key}: ${String(message)}`);
      });
      return messages.join(". ") || "Failed to create recipe.";
    }

    return "Failed to create recipe. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setErrorMsg("");
    setSubmitting(true);

    try {
      const data = new FormData();
      const prepMinutes = toMinutes(formData.prep_time_value, formData.prep_time_unit);
      const cookMinutes = toMinutes(formData.cook_time_value, formData.cook_time_unit);

      Object.entries(formData).forEach(([key, value]) => {
        if (
          key === "prep_time_value" ||
          key === "prep_time_unit" ||
          key === "cook_time_value" ||
          key === "cook_time_unit"
        ) {
          return;
        }

        if (value !== null && value !== undefined && value !== "") {
          if (key === "ingredients" || key === "instructions") {
            data.append(key, JSON.stringify(value.split("\n").filter(Boolean)));
          } else if (key === "images" || key === "videos") {
            value.forEach((file) => data.append(key, file));
          } else if (key === "tag_names") {
            value.forEach((tag) => data.append("tag_names", tag));
          } else {
            data.append(key, value);
          }
        }
      });

      if (prepMinutes !== "") {
        data.append("prep_time", prepMinutes);
      }

      if (cookMinutes !== "") {
        data.append("cook_time", cookMinutes);
      }

      await axiosInstance.post("/api/recipes/create/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      window.dispatchEvent(new Event("tag-changed"));
      guardPushedRef.current = false;
      setShowSuccess(true);
      toast.success("Recipe created.");
    } catch (err) {
      console.error(err);
      const message = getCreateRecipeErrorMessage(err);
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
      <div className="bg-white dark:bg-gray-800 shadow-xl dark:shadow-gray-900/50 rounded-2xl p-8 w-full max-w-2xl">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="type-eyebrow mb-2">
              New recipe
            </p>
            <h1 className="type-page-title">Create a Recipe</h1>
            <p className="type-muted mt-1">
              Start from scratch, a file, a link, or your phone.
            </p>
          </div>
          <div className="inline-flex flex-wrap items-center gap-1.5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/50 p-1.5">
            <input
              ref={uploadRecipeInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleUploadRecipeFile}
              className="hidden"
              aria-label="Upload recipe file"
            />
            <button
              type="button"
              onClick={() => uploadRecipeInputRef.current?.click()}
              disabled={importingRecipe || submitting}
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-white hover:text-emerald-700 dark:hover:bg-gray-800 dark:hover:text-emerald-300 transition disabled:opacity-50"
              aria-label="Upload recipe"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M7 9l5-5 5 5M12 4v12" />
              </svg>
              {importingRecipe ? "Uploading..." : "Upload"}
            </button>
            <button
              type="button"
              onClick={() => setShowImportUrlModal(true)}
              disabled={importingUrl || importingRecipe || submitting}
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-white hover:text-amber-700 dark:hover:bg-gray-800 dark:hover:text-amber-300 transition disabled:opacity-50"
              aria-label="Import recipe from URL"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-1.414 1.414a4 4 0 01-5.657-5.657l1.414-1.414M10.172 13.828a4 4 0 010-5.656l1.414-1.414a4 4 0 115.657 5.657l-1.414 1.414" />
              </svg>
              {importingUrl ? "Importing..." : "URL"}
            </button>
            <button
              type="button"
              onClick={startScan}
              className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-white hover:text-blue-700 dark:hover:bg-gray-800 dark:hover:text-blue-300 transition"
              aria-label="Scan recipe with phone"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Scan
            </button>
          </div>
        </div>
        <RecipeForm formData={formData}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                      if (hasUnsavedData()) {
                        pendingLeaveActionRef.current = "recipes";
                        setShowLeaveConfirm(true);
                      } else {
                        navigate("/recipes");
                      }
                    }}
                    submitButtonText={submitting ? "Creating..." : "Create Recipe"}
                    disabled={submitting}
                    errorMsg={errorMsg}
        />
      </div>

      {/* Scan QR Modal */}
      {showScanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
            <h3 className="type-section-title mb-2">Scan Recipe</h3>
            <p className="type-muted mb-6">
              Open this on your phone and snap the recipe.
            </p>
            <div className="flex justify-center mb-6">
              <div className="bg-white p-3 rounded-xl">
                <QRCodeSVG value={scanUrl} size={200} />
              </div>
            </div>
            {scanStatus === "waiting" && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                Waiting for photo...
              </div>
            )}
            {scanStatus === "failed" && (
              <p className="text-sm text-red-500">Scan failed. Please try again.</p>
            )}
            <button
              onClick={closeScanModal}
              className="mt-4 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showImportUrlModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-md w-full">
            <h3 className="type-section-title mb-2">Import From URL</h3>
            <p className="type-muted mb-4">
              Paste a public recipe link to prefill the form.
            </p>
            <label htmlFor="recipe-import-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipe URL
            </label>
            <input
              id="recipe-import-url"
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              placeholder="https://example.com/recipe"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowImportUrlModal(false);
                  setImportUrl("");
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImportUrl}
                disabled={importingUrl}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition"
              >
                {importingUrl ? "Working..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <ConfirmModal
          title="Recipe Created!"
          message="Your recipe has been added to your collection."
          confirmText="View Recipe"
          cancelText="Create Another"
          variant="success"
          onConfirm={() => {
            const vis = formData.visibility || "public";
            navigate(`/recipes?view=${vis}`);
          }}
          onCancel={() => {
            setShowSuccess(false);
            setFormData({
              title: "", description: "", ingredients: "", instructions: "",
              prep_time_value: "", prep_time_unit: "min",
              cook_time_value: "", cook_time_unit: "min",
              servings: "", difficulty: "",
              category: "", diet_preference: "", visibility: requestedVisibility,
              source_author_name: "", source_url: "",
              images: [], videos: [], tag_names: [],
            });
          }}
          onDismiss={() => {
            const vis = formData.visibility || "public";
            navigate(`/recipes?view=${vis}`);
          }}
        />
      )}

      {showLeaveConfirm && (
        <ConfirmModal
          title="Unsaved Changes"
          message="You have unsaved changes. If you leave now, your recipe will be lost."
          variant="danger"
          confirmText="Leave"
          cancelText="Stay"
          onConfirm={confirmLeave}
          onCancel={() => {
            if (!guardPushedRef.current) {
              window.history.pushState({ guard: true }, '', window.location.href);
              guardPushedRef.current = true;
            }
            pendingLeaveActionRef.current = "back";
            setShowLeaveConfirm(false);
          }}
        />
      )}
    </div>
  );
}
